import { Clear, InfoOutlined, Search } from '@mui/icons-material';
import {
  Box,
  InputAdornment,
  List,
  ListItem,
  Stack,
  Tooltip,
} from '@mui/material';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { usePostEmployeeSearch } from '@legacy/apis/resourcePlannerAPI';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@legacy/components/mui-components';
import { ResponseHandler } from '@legacy/components/utils/ResponseHandler';
import { useDebounce } from 'use-debounce';
import {
  useGetDefaultDates,
  useGetFlattenedFilterItems,
  useKeyboardControl,
} from '../../components/AssignFlow/hooks';
import { EmployeeWithCompetenceSkeleton } from '../../components/AssignFlow/components/EmployeeWithCompetence';
import {
  FilterItem,
  defaultFilterItem,
} from '../../components/AssignFlow/components/FilterItems/FilterItem';
import {
  FilterItemAvailability,
  IFilterAvailability,
} from '../../components/AssignFlow/components/FilterItems/FilterItemAvailability';
import {
  FilterItemCompetence,
  IFilterCompetence,
} from '../../components/AssignFlow/components/FilterItems/FilterItemCompetence';
import { ResourceList } from './components/ResourceList';
import { defaultFilters } from './SearchForResourceDialog.constants';
import {
  EResourceFilterNames,
  TResourceDefaultFilters,
} from './SearchForResourceDialog.types';
import styles from './SearchForResourceDialog.module.scss';
import { CompetenceValues } from '../../components/AssignFlow/components/FilterItems/FilterItemCompetence/FilterItemCompetence.types';

export const SearchForResourceDialog = () => {
  const { t } = useTranslation('assignFlow');
  const navigate = useNavigate();
  const { id: workItemSourceReferenceId } = useParams<{ id: string }>() as {
    id: string;
  };
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId') || '';
  const name = searchParams.get('name') || '';
  const startsAtParam = searchParams.get('startsAt');
  const startsAt = startsAtParam ? new Date(startsAtParam) : new Date();

  const endsAtParam = searchParams.get('endsAt');
  const endsAt = endsAtParam ? new Date(endsAtParam) : new Date();

  const [defaultStartDate, defaultEndDate] = useGetDefaultDates([
    startsAt,
    endsAt,
  ]);

  const resourceFilterItems = useGetFlattenedFilterItems(EResourceFilterNames);
  const showDepartmentFilter = !!resourceFilterItems.find(
    ({ filterName }) => filterName === 'ProjectDepartment',
  )?.id;
  const showLegalEntityFilter = !!resourceFilterItems.find(
    ({ filterName }) => filterName === 'LegalEntityProject',
  )?.id;

  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 500);
  const [selectedFilters, setSelectedFilters] =
    useState<Partial<TResourceDefaultFilters>>(defaultFilters);
  const [competence, setCompetence] = useState<IFilterCompetence>({
    isInclude: true,
    values: [],
  } as IFilterCompetence);
  const [availability, setAvailability] = useState<IFilterAvailability>({
    periodEnd: defaultEndDate,
    periodStart: defaultStartDate,
  });

  const cleanUp = () => {
    setQuery('');
    setSelectedFilters(defaultFilters);
    setCompetence(defaultFilterItem);
    setAvailability({
      periodEnd: defaultEndDate,
      periodStart: defaultStartDate,
    });
    navigate('/projects/resource-planner');
  };

  const {
    dialogOnKeyDown,
    initialFocusElement,
    searchContainerResults,
    selectItemIndex,
    setSelectItemIndex,
    textFieldOnKeyDown,
  } = useKeyboardControl({ hasLazyLoad: true });

  // Handle resources
  const {
    data,
    lastElementRef,
    hasNextPage,
    isError,
    isLoading,
    isFetchingNextPage,
  } = usePostEmployeeSearch({
    competence,
    department: selectedFilters.ProjectDepartment,
    endDate: availability.hours ? availability.periodEnd : undefined,
    hours: availability.hours,
    legalEntity: selectedFilters.LegalEntityProject,
    manager: selectedFilters.ApprovalManager,
    query: debouncedQuery,
    startDate: availability.hours ? availability.periodStart : undefined,
    taskId: workItemSourceReferenceId,
  });

  const employeeList = useMemo(
    () => data.map((properties) => properties),
    [data],
  );

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  }, []);

  const handleClearClick = useCallback(() => {
    setQuery('');
  }, []);

  const handleCompetenceChange = useCallback((c: CompetenceValues[]) => {
    setCompetence({
      isInclude: true,
      values: c,
    });
  }, []);

  const handleFilterChange = useCallback(
    (filterName: string) => (v: any) => {
      setSelectedFilters((prev) => ({
        ...prev,
        [filterName]: v,
      }));
    },
    [],
  );

  return (
    <Dialog
      data-automation-id="SearchForResourceDialog"
      fullWidth
      maxWidth="md"
      onClose={cleanUp}
      onKeyDown={dialogOnKeyDown}
      open
      PaperProps={{ sx: { overflowY: 'unset' } }}
    >
      <DialogTitle
        component={Box}
        data-automation-id="SearchForResourceDialogTitle"
      >
        <Stack gap={2}>
          <Typography lineHeight={1.6} variant="h3" component="h2">
            <Trans
              i18nKey="AssignTaskToEmployeeHeader"
              key="AssignTaskToEmployeeHeader"
              defaults={t('AssignTaskToEmployeeHeader')}
              ns="assignFlow"
              values={{ task: name }}
              components={[<strong key={0}>{name}</strong>]}
            />
          </Typography>
          <Stack alignItems="center" direction="row" gap={2}>
            <TextField
              ariaLabel={t('SearchForEmployeesInputLabel')}
              data-automation-id="SuperSearchForResource"
              fullWidth
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {query ? (
                        <IconButton
                          data-automation-id="SearchForResourceInputClearButton"
                          onClick={handleClearClick}
                          size="small"
                          title={t('ClearSearchButtonText')}
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      ) : null}
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                },
                htmlInput: { autoFocus: true, autoComplete: 'off' },
              }}
              inputRef={initialFocusElement}
              label={t('SearchForEmployeesInputLabel')}
              onChange={handleInputChange}
              onKeyDown={textFieldOnKeyDown}
              value={query}
            />
            <Tooltip
              tabIndex={0}
              title={
                <Stack>
                  <Typography
                    component="h3"
                    fontWeight={700}
                    lineHeight={1.5}
                    variant="h4"
                  >
                    {t('SearchForEmployeesTooltipHeading')}
                  </Typography>
                  <Typography variant="label" sx={{ whiteSpace: 'pre-line' }}>
                    {t('SearchForEmployeesTooltip')}
                  </Typography>
                </Stack>
              }
            >
              <InfoOutlined />
            </Tooltip>
          </Stack>
          <Stack direction="row" flex={1} flexWrap="wrap">
            {resourceFilterItems.map((i) => (
              <FilterItem
                {...i}
                data-automation-id={`FilterItem-${i.filterName}`}
                filterName={i.filterName}
                key={String(i.id)}
                onChange={handleFilterChange(i.filterName)}
              />
            ))}
            <FilterItemCompetence onChange={(c) => handleCompetenceChange(c)} />
            <FilterItemAvailability
              dates={[startsAt, endsAt]}
              onChange={setAvailability}
              value={availability}
            />
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent
        dividers
        onMouseEnter={() => setSelectItemIndex(-1)}
        sx={{ pt: 2, zIndex: 0 }}
      >
        <ResponseHandler
          EmptyComponent={
            <Typography textAlign="center">{t('NoResults')}</Typography>
          }
          isError={isError}
          isEmpty={!employeeList.length}
          isLoading={isLoading}
          LoadingComponent={
            <List className={styles.list} sx={{ minHeight: 460 }}>
              <ListItem disablePadding>
                <EmployeeWithCompetenceSkeleton />
              </ListItem>
              <ListItem disablePadding>
                <EmployeeWithCompetenceSkeleton />
              </ListItem>
              <ListItem disablePadding>
                <EmployeeWithCompetenceSkeleton />
              </ListItem>
              <ListItem disablePadding>
                <EmployeeWithCompetenceSkeleton />
              </ListItem>
              <ListItem disablePadding>
                <EmployeeWithCompetenceSkeleton />
              </ListItem>
              <ListItem disablePadding>
                <EmployeeWithCompetenceSkeleton />
              </ListItem>
            </List>
          }
        >
          <ResourceList
            employeeList={employeeList}
            searchContainerResults={searchContainerResults}
            selectItemIndex={selectItemIndex}
            showDepartmentFilter={showDepartmentFilter}
            showLegalEntityFilter={showLegalEntityFilter}
            startsAt={startsAt}
            endsAt={endsAt}
            from={name}
            projectId={projectId}
            workItemSourceReferenceId={workItemSourceReferenceId}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            query={query}
            observerRef={lastElementRef}
          />
        </ResponseHandler>
      </DialogContent>
      <DialogActions>
        <Button
          data-automation-id="CancelButton"
          onClick={cleanUp}
          variant="outlined"
        >
          {t('CancelButtonText')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
